import { DialogContextProps } from "@interfaces/System/DialogContextProps";
import { Button, DialogActions, DialogContent } from "@material-ui/core";
import { ModalContentLoading } from "@components/Common/Modal";
import React, { useRef } from "react";
import { staffOrderService } from "@services/Staff";
import { useStoreon } from "storeon/react";
import { TableEvents, TableState } from "@store/table-module";
import { useSnackbar } from "notistack";
import { useAsync } from "@hooks";
import { CreateStaffOrderItemDto, UpdateStaffOrderItemDto } from "@dto/StaffOrderItemDto";
import { StaffOrderItemForm } from "@components/Forms/StaffOrder";

const StaffOrderItemDialogContext = ({ onClose, modalProps }: DialogContextProps) => {
    const { itemId } = modalProps;

    const formRef = useRef<HTMLFormElement>();
    const { enqueueSnackbar } = useSnackbar();
    const { dispatch: tableDispatch } = useStoreon<TableState, TableEvents>();

    const { data, loadingStatus } = useAsync(staffOrderService.getOneStaffOrderItem, {
        functionArgs: [itemId],
        canLoad: !!itemId,
    });

    const invokeSubmit = () => {
        formRef.current!.handleSubmit();
    };

    const handleCreateStaffOrderItem = async (values) => {
        try {
            const dto = staffOrderService.plainToClass(CreateStaffOrderItemDto, values);
            await staffOrderService.createStaffOrderItem(dto);
            enqueueSnackbar("Заказ успешно создан", {
                variant: "success",
            });
            onClose();
            tableDispatch("table/update/data");
        } catch (err) {
            const message = err.response?.data?.error_tooltip;
            enqueueSnackbar(message || "Произошла ошибка при создании заказа", {
                variant: "error",
            });
        }
    };

    const handleUpdateStaffOrderItem = async (values) => {
        if (!data) return;

        try {
            const dto = staffOrderService.plainToClass(UpdateStaffOrderItemDto, values);
            await staffOrderService.updateStaffOrderItem(data.id, dto);
            enqueueSnackbar("Элемент заказа успешно обновлен", {
                variant: "success",
            });
            onClose();
            tableDispatch("table/update/data");
        } catch (err) {
            const message = err.response?.data?.error_tooltip;
            enqueueSnackbar(message || "Произошла ошибка при обновлении элемента заказа", {
                variant: "error",
            });
        }
    };

    return (
        <>
            <DialogContent>
                {loadingStatus === "loading" && <ModalContentLoading />}

                {(loadingStatus === "loaded" || loadingStatus === "inactivity") && (
                    <StaffOrderItemForm
                        ref={formRef}
                        data={data}
                        onSubmit={data ? handleUpdateStaffOrderItem : handleCreateStaffOrderItem}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button color="default" onClick={onClose}>
                    Отмена
                </Button>
                <Button color="primary" variant="contained" onClick={invokeSubmit}>
                    Сохранить
                </Button>
            </DialogActions>
        </>
    );
};

export default StaffOrderItemDialogContext;
